import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, errorMessage, newIdempotencyKey, unwrap, type EventData, type Restriction, type SnapCandidate, type SnapPathResponse } from "../../api/client";
import { useSession } from "../../auth/session";
import { formatDateTime, formatDistance, fromParisInput, toParisInput } from "../../domain/format";
import { fr } from "../../i18n/fr";
import type { LatLonPt } from "../../map/RouteMap";
import { Confirm, Field, Notice, useOffline } from "../../ui/components";
import { useForeignUpdate } from "../../ui/useForeignUpdate";

interface Props {
  eventId: string;
  event: EventData;
  restriction: Restriction;
  isNew?: boolean;
  readOnly?: boolean;
  activeNow: boolean;
  onRedraw(): void;
  /** the operator picked a snapping candidate: the point moves onto that road (SW-RS-03) */
  onMoveTo?(p: LatLonPt): void;
  /** "create next": a new restriction of the same kind starting when this one ends (SW-RS-21) */
  onChain?(previous: Restriction): void;
  /** snapped line of a stretch (null while computing or for a point), drawn by the map for a course (1.1.1) */
  onSnapLine?(line: NonNullable<SnapPathResponse["geometry"]> | null): void;
  onDone(msg?: string, saved?: Restriction): void | Promise<void>;
  onCancel(): void;
}

type Snap = { kind: "point"; candidates: SnapCandidate[]; radiusM: number } | { kind: "path"; result: SnapPathResponse };

/** Restriction edition (SW-WS-10 to SW-WS-13). The form only holds the fields of the chosen kind. */
export function RestrictionForm({ eventId, event, restriction: r0, isNew, readOnly, activeNow, onRedraw, onMoveTo, onChain, onSnapLine, onDone, onCancel }: Props) {
  const s = useSession();
  const offline = useOffline();
  const [r, setR] = useState<Restriction>({ ...r0, enabled: r0.enabled !== false });
  // the geometry comes from the map (redraw, candidate picked): the other fields keep what the operator typed
  useEffect(() => { setR((x) => (x.geometry === r0.geometry ? x : { ...x, geometry: r0.geometry })); }, [r0.geometry]);
  const [customPeriod, setCustomPeriod] = useState(!!(r0.startAt || r0.endAt));
  const [snap, setSnap] = useState<Snap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<Restriction | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmLive, setConfirmLive] = useState(false);
  const [idemKey] = useState(newIdempotencyKey);
  const adopt = (v: Restriction) => { setR({ ...v, enabled: v.enabled !== false }); setCustomPeriod(!!(v.startAt || v.endAt)); };
  // SW-RS-14: a version saved by another operator while the form is open never erases the input in progress
  const foreign = useForeignUpdate(r0, r, isNew, adopt, (v) => ({ ...v, enabled: v.enabled !== false, geometry: undefined }));
  const canDelete = !isNew && !readOnly && (s.isAdmin || event.status === "DRAFT");

  // snapping preview of the geometry (SW-WS-11), same radius as the engine (SW-RS-02): the server applies its default
  useEffect(() => {
    let cancelled = false;
    const g = r0.geometry;
    setSnap(null);
    (async () => {
      try {
        if (g.type === "Point") {
          const res = unwrap(await api.POST("/snap/point", { body: { position: { lat: g.coordinates[1], lon: g.coordinates[0] } } }));
          if (!cancelled) setSnap({ kind: "point", candidates: res.candidates, radiusM: res.radiusM });
        } else {
          const res = unwrap(await api.POST("/snap/path", { body: { waypoints: g.coordinates.map((c) => ({ lat: c[1], lon: c[0] })) } }));
          if (!cancelled) setSnap({ kind: "path", result: res });
        }
      } catch { if (!cancelled) setSnap(null); }
    })();
    return () => { cancelled = true; };
  }, [r0.geometry]);
  const snapLine = snap?.kind === "path" ? snap.result.geometry ?? null : null;
  useEffect(() => { onSnapLine?.(snapLine); }, [snapLine]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onSnapLine?.(null), []); // eslint-disable-line react-hooks/exhaustive-deps

  // suggested label from the street names when the operator left it empty (never for a course: its label is typed)
  useEffect(() => {
    if (r.label || !snap || r.kind === "COURSE" || r.kind === "CROSSING_POINT") return;
    const names = snap.kind === "point" ? snap.candidates.slice(0, 1).map((c) => c.streetName) : snap.result.streetNames;
    const label = names.filter(Boolean).slice(0, 3).join(" / ");   // same separator as the server (EventService.labelFor)
    if (label && isNew) setR((x) => (x.label ? x : { ...x, label }));
  }, [snap, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof Restriction>(k: K, v: Restriction[K]) => setR((x) => ({ ...x, [k]: v }));

  // SW-RS-01: nothing is saved without a successful snapping (a barrier drawn as a line closes what it crosses)
  const snapBlocked = snap != null && (snap.kind === "point" ? snap.candidates.length === 0 : r.kind !== "BARRIER" && snap.result.edgeCount === 0);

  const validate = (): string | null => {
    if (r.kind === "BARRIER" && r.crossable == null) return `${fr.restriction.crossable} : ${fr.common.required}`;
    if (r.kind === "COURSE" && !r.label?.trim()) return `${fr.restriction.courseLabel} : ${fr.common.required}`;
    if (r.kind === "CROSSING_POINT" && !r.label?.trim()) return `${fr.restriction.crossingPointLabel} : ${fr.common.required}`;
    if (r.kind === "CROSSING_POINT" && !r.courseId) return `${fr.restriction.crossingPointCourse} : ${fr.common.required}`;
    if (r.kind === "HEIGHT_LIMIT" && !(r.maxHeightM != null && r.maxHeightM > 0 && r.maxHeightM < 10)) return `${fr.restriction.maxHeight} : ${fr.common.required}`;
    if (customPeriod && r.startAt && r.endAt && Date.parse(r.endAt) <= Date.parse(r.startAt)) return fr.events.endBeforeStart;
    return null;
  };

  const save = async (): Promise<Restriction | null> => {
    const body: Restriction = { ...r, startAt: customPeriod ? r.startAt : undefined, endAt: customPeriod ? r.endAt : undefined };
    if (body.kind !== "BARRIER") { delete body.crossable; delete body.deviceType; delete body.blockedBearingDeg; }
    if (body.kind !== "HEIGHT_LIMIT") delete body.maxHeightM;
    if (body.kind !== "ONEWAY_REVERSED") delete body.oldDirection;
    if (body.kind !== "CROSSING_POINT") delete body.courseId;
    setBusy(true); setError(null); setConflict(null);
    try {
      const res = isNew
        ? unwrap(await api.POST("/events/{id}/restrictions", { params: { path: { id: eventId } }, body, headers: { "Idempotency-Key": idemKey } }))
        : unwrap(await api.PATCH("/events/{id}/restrictions/{rid}", { params: { path: { id: eventId, rid: r.id } }, body }));
      await onDone(res.zoneExtended ? `${fr.restriction.saved} ${fr.workspace.zoneExtended}` : fr.restriction.saved, res.item);
      return res.item;
    } catch (x) {
      if (x instanceof ApiError && x.code === "VERSION_CONFLICT" && x.current) setConflict(x.current as Restriction);
      setError(errorMessage(x, fr.errors));
      return null;
    } finally { setBusy(false); }
  };

  /** SW-RS-21: saves when needed, then opens the next restriction starting at the end of this one */
  const chain = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    if (isNew) { const saved = await save(); if (saved) onChain?.(saved); } else onChain?.({ ...r, startAt: customPeriod ? r.startAt : undefined, endAt: customPeriod ? r.endAt : undefined });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    if (activeNow && !confirmLive) { setConfirmLive(true); return; }
    void save();
  };

  const del = async () => {
    setBusy(true); setError(null);
    try {
      unwrap(await api.DELETE("/events/{id}/restrictions/{rid}", { params: { path: { id: eventId, rid: r.id } } }));
      await onDone(fr.restriction.deleted);
    } catch (x) { setError(errorMessage(x, fr.errors)); setConfirmDelete(false); } finally { setBusy(false); }
  };

  const g = r0.geometry;
  // chantier Points de cisaillement (1.3.0): the courses a point may belong to, the points of a course
  const courses = (event.restrictions ?? []).filter((x) => x.kind === "COURSE");
  const crossingPoints = r.kind === "COURSE" && r.id ? (event.restrictions ?? []).filter((x) => x.kind === "CROSSING_POINT" && x.courseId === r.id) : [];
  return (
    <form className="card stack form" onSubmit={submit}>
      <div className="row between">
        <h3>{isNew ? fr.restriction.new(fr.kind[r.kind]) : `${fr.kind[r.kind]}${r.label ? ` · ${r.label}` : ""}`}</h3>
        <button type="button" className="icon" onClick={onCancel} aria-label={fr.common.close}>×</button>
      </div>
      <p className="muted small">{fr.restriction.kindHelp[r.kind]}</p>

      <div className="field"><span>{fr.restriction.geometry}</span>
        <div className="row between small">
          <span>{g.type === "Point" ? fr.restriction.point : r.kind === "COURSE" ? fr.restriction.coursePath(g.coordinates.length) : fr.restriction.path(g.coordinates.length)}</span>
          {!readOnly && <button type="button" onClick={onRedraw}>{fr.restriction.redraw}</button>}
        </div>
        {snap && (
          <div className="snap small">
            {snap.kind === "point" ? (
              snap.candidates.length === 0 ? <span className="warn">{fr.restriction.snapping_none(Math.round(snap.radiusM))}</span> : (
                <>
                  <span>{fr.restriction.snapNotes} :</span>
                  {!readOnly && onMoveTo && <span className="muted"> {fr.restriction.snapPick}</span>}
                  {snap.candidates.slice(0, 3).map((c) => (
                    <button key={c.segment} type="button" className="candidate link" disabled={readOnly || !onMoveTo}
                      onClick={() => onMoveTo?.({ lat: c.position.lat, lon: c.position.lon })}>
                      {c.streetName || fr.restriction.unnamed} ({formatDistance(c.distanceM)})
                    </button>
                  ))}
                </>
              )
            ) : (
              <>
                <span>{fr.restriction.snapNotes} : {snap.result.streetNames.length ? snap.result.streetNames.join(", ") : fr.common.none} · {formatDistance(snap.result.lengthM)}</span>
                {snapBlocked && <div className="warn">{fr.restriction.snapNoPath}</div>}
                {snap.result.warnings.length > 0 && <ul className="warn">{snap.result.warnings.map((w) => <li key={w}>{fr.warning[w] ?? w}</li>)}</ul>}
              </>
            )}
          </div>
        )}
      </div>

      {r.kind === "COURSE" ? (
        <>
          <Field label={`${fr.restriction.courseLabel} (${fr.common.required})`}><input value={r.label ?? ""} onChange={(e) => set("label", e.target.value)} maxLength={120} disabled={readOnly} /></Field>
          {crossingPoints.length > 0 && (
            <div className="small">
              <strong>{fr.restriction.crossingPoints(crossingPoints.length)}</strong> : {crossingPoints.map((p) => p.label).join(", ")}
              <div className="muted">{fr.restriction.crossingPointsHelp}</div>
            </div>
          )}
        </>
      ) : r.kind === "CROSSING_POINT" ? (
        <>
          <Field label={`${fr.restriction.crossingPointLabel} (${fr.common.required})`}><input value={r.label ?? ""} onChange={(e) => set("label", e.target.value)} maxLength={120} disabled={readOnly} /></Field>
          <Field label={`${fr.restriction.crossingPointCourse} (${fr.common.required})`} hint={fr.restriction.crossingPointSnap(30)}>
            <select value={r.courseId ?? ""} onChange={(e) => set("courseId", e.target.value || undefined)} disabled={readOnly}>
              <option value="">{fr.restriction.crossingPointPickCourse}</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.label || c.id}</option>)}
            </select>
          </Field>
        </>
      ) : (
        <Field label={fr.restriction.label}><input value={r.label ?? ""} onChange={(e) => set("label", e.target.value)} maxLength={120} disabled={readOnly} /></Field>
      )}

      {r.kind === "BARRIER" && (
        <>
          <div className="field"><span>{fr.restriction.crossable} <small className="muted">({fr.common.required})</small></span>
            <div className="segmented">
              <button type="button" className={r.crossable === false ? "active" : ""} disabled={readOnly} onClick={() => set("crossable", false)}>{fr.common.no}</button>
              <button type="button" className={r.crossable === true ? "active" : ""} disabled={readOnly} onClick={() => set("crossable", true)}>{fr.common.yes}</button>
            </div>
            <small className="muted">{fr.restriction.crossableHelp}</small>
          </div>
          <div className="grid2">
            <Field label={fr.restriction.deviceType}>
              <select value={r.deviceType ?? ""} onChange={(e) => set("deviceType", (e.target.value || undefined) as Restriction["deviceType"])} disabled={readOnly}>
                <option value="">{fr.common.unknown}</option>
                {Object.entries(fr.restriction.deviceTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            {g.type === "Point" && (
              <Field label={fr.restriction.blockedBearing} hint={fr.restriction.blockedBearingHelp}>
                <input type="number" min={0} max={359} step={1} value={r.blockedBearingDeg ?? ""} disabled={readOnly}
                  onChange={(e) => set("blockedBearingDeg", e.target.value === "" ? undefined : Number(e.target.value))} />
              </Field>
            )}
          </div>
        </>
      )}
      {r.kind === "HEIGHT_LIMIT" && (
        <Field label={fr.restriction.maxHeight}>
          <input type="number" min={0.5} max={9.99} step={0.05} required value={r.maxHeightM ?? ""} disabled={readOnly}
            onChange={(e) => set("maxHeightM", e.target.value === "" ? undefined : Number(e.target.value))} />
        </Field>
      )}
      {r.kind === "ONEWAY_REVERSED" && (
        <Field label={fr.restriction.oldDirection}>
          <select value={r.oldDirection ?? "STRICT"} onChange={(e) => set("oldDirection", e.target.value as Restriction["oldDirection"])} disabled={readOnly}>
            {Object.entries(fr.oldDirection).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {r.oldDirection && fr.oldDirectionHint[r.oldDirection] && <div className="muted small">{fr.oldDirectionHint[r.oldDirection]}</div>}
        </Field>
      )}

      <div className="field"><span>{fr.restriction.period}</span>
        <div className="segmented">
          <button type="button" className={!customPeriod ? "active" : ""} disabled={readOnly} onClick={() => setCustomPeriod(false)}>{fr.restriction.eventPeriod}</button>
          <button type="button" className={customPeriod ? "active" : ""} disabled={readOnly} onClick={() => setCustomPeriod(true)}>{fr.restriction.custom}</button>
        </div>
        {customPeriod && (
          <div className="grid2">
            <Field label={fr.restriction.startAt}><input type="datetime-local" value={r.startAt ? toParisInput(r.startAt) : ""} disabled={readOnly} onChange={(e) => set("startAt", e.target.value ? fromParisInput(e.target.value) : undefined)} /></Field>
            <Field label={fr.restriction.endAt}><input type="datetime-local" value={r.endAt ? toParisInput(r.endAt) : ""} disabled={readOnly} onChange={(e) => set("endAt", e.target.value ? fromParisInput(e.target.value) : undefined)} /></Field>
          </div>
        )}
        {!customPeriod && <small className="muted">{formatDateTime(event.startAt)} → {formatDateTime(event.endAt)}</small>}
      </div>

      <label className="check"><input type="checkbox" checked={r.enabled !== false} disabled={readOnly} onChange={(e) => set("enabled", e.target.checked)} /> {fr.restriction.enabled}</label>
      <Field label={fr.restriction.comment}><textarea rows={2} value={r.comment ?? ""} disabled={readOnly} onChange={(e) => set("comment", e.target.value)} /></Field>

      {foreign.changed && !conflict && (
        <Notice kind="warn">
          {fr.common.changedMeanwhile(foreign.changed.updatedBy, foreign.changed.updatedAt ? formatDateTime(foreign.changed.updatedAt) : undefined)}
          <button type="button" onClick={() => { adopt(foreign.changed!); foreign.dismiss(); }}>{fr.common.reloadCurrent}</button>
        </Notice>
      )}
      {conflict && (
        <Notice kind="warn">
          {fr.common.conflict}
          <div className="small">{fr.common.version} {conflict.version}{conflict.updatedBy ? ` ${fr.common.by} ${conflict.updatedBy}` : ""}{conflict.updatedAt ? `, ${formatDateTime(conflict.updatedAt)}` : ""}</div>
          <button type="button" onClick={() => { adopt(conflict); setConflict(null); setError(null); foreign.dismiss(); }}>{fr.common.reloadCurrent}</button>
        </Notice>
      )}
      {error && !conflict && <div className="error small">{error}</div>}
      {!readOnly && !isNew && r.updatedAt && <div className="muted small">{fr.common.version} {r.version} · {formatDateTime(r.updatedAt)}{r.updatedBy ? ` ${fr.common.by} ${r.updatedBy}` : ""}</div>}

      {!readOnly && (
        <div className="row between wrap">
          <div>{canDelete && <button type="button" className="danger ghost" onClick={() => setConfirmDelete(true)}>{fr.common.delete}</button>}</div>
          <div className="row">
            {onChain && customPeriod && r.endAt && (
              <button type="button" disabled={busy || offline || snapBlocked} title={fr.restriction.chainHelp} onClick={() => void chain()}>{fr.restriction.chain}</button>
            )}
            <button type="button" onClick={onCancel}>{fr.common.cancel}</button>
            <button type="submit" className="primary" disabled={busy || offline || snapBlocked} title={offline ? fr.common.offlineShort : undefined}>{busy ? fr.common.saving : fr.common.save}</button>
          </div>
        </div>
      )}
      <Confirm open={confirmDelete} title={fr.common.delete} text={crossingPoints.length ? `${fr.restriction.deleteConfirm} ${fr.restriction.deleteCourseWithPoints(crossingPoints.length)}` : fr.restriction.deleteConfirm} danger confirmLabel={fr.common.delete} onConfirm={del} onClose={() => setConfirmDelete(false)} />
      <Confirm open={confirmLive} title={fr.workspace.publish} text={fr.workspace.activeWarning} confirmLabel={fr.common.save} onConfirm={async () => { setConfirmLive(false); await save(); }} onClose={() => setConfirmLive(false)} />
    </form>
  );
}
